<script setup>
import DpButton from "./DpButton";
import DpExpand from "./DpExpand";
import DpIcon from "./DpIcon";
import { isEmpty, get } from 'lodash';
import { ref, defineProps, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const props = defineProps({
  headline: { type: String },
  description: { type: String },
  primaryButton: { type: Object, default: undefined },
  secondaryButton: { type: Object, default: undefined },
  closable: { type: Boolean, default: true }, // TODO: deprecated
  isDismissed: { type: Boolean, default: false },
  isClosable: { type: Boolean, default: true }
})
const isReminderVisible = ref(!props.isDismissed);
watch(() => props.isDismissed, (value) => {
  isReminderVisible.value = !(value);
})

function dismiss() {
  isReminderVisible.value = false;
}

const isDefaultSlotActive = ref(false);

async function handleReminderAction(button) {
  if (button === undefined)
    return;
  if (button.action !== undefined) {
    await button.action();
  }
  if (button.toggleDefaultSlot !== undefined && button.toggleDefaultSlot === true) {
    isDefaultSlotActive.value = !(isDefaultSlotActive.value);
  }
}
</script>
<script>
export default {
  name: "DpReminder",
}
</script>

<template>
  <div class="dp-reminder" v-show="isReminderVisible">
    <div v-if="props.closable && props.isClosable"
         tabindex="0"
         @click="dismiss"
         class="dp-flex justify-content__flex-end dp-reminder__dismiss">
      <DpIcon icon="close" style="font-size: 2rem;"/>
      {{ t('close.buttonLabel') }}
    </div>
    <DpExpand>
      <div v-show="isDefaultSlotActive">
        <slot></slot>
      </div>
    </DpExpand>
    <DpExpand>
      <div v-show="!isDefaultSlotActive">
        <slot name="phrases">
          <h3 v-if="props.headline" class="text-align-center">{{ props.headline }}</h3>
          <p v-if="props.description">{{ props.description }}</p>
        </slot>
        <slot name="extraContent"></slot>
        <div v-if="!isEmpty(props.primaryButton) || !isEmpty(props.secondaryButton)"
             class="dp-reminder__buttons dp-flex column">
          <DpButton v-if="!isEmpty(props.secondaryButton)" purpose="secondaryAction"
                    @click="handleReminderAction(props.secondaryButton)"
          >{{ get(props.secondaryButton, 'label') }}
          </DpButton>
          <DpButton v-if="!isEmpty(props.primaryButton)" purpose="primaryAction"
                    @click="handleReminderAction(props.primaryButton)"
          >{{ get(props.primaryButton, 'label') }}
          </DpButton>
        </div>
      </div>
    </DpExpand>
  </div>
</template>

<style lang="scss">
.dp-reminder {
  position: relative;
  padding: 1em;
  background-color: var(--use-theme__background-color);
  border: 2px solid var(--use-theme__ui-gradient-2);
  border-radius: 8px;
  box-shadow: 0 5px 20px var(--use-theme__ui-gradient-2);
  overflow: hidden;
  margin: 1rem auto;

  .dp-reminder__buttons {
    .dp-button {
      margin: 1em auto;
      display: block;
    }
  }

  .dp-reminder__dismiss {
    cursor: pointer;
  }
}
</style>

<i18n>
{
  "ar-AE": {
    "close": {
      "buttonLabel": "تذكير بالإغلاق"
    }
  },
  "ar-EG": {
    "close": {
      "buttonLabel": "تذكير بالإغلاق"
    }
  },
  "ar-SA": {
    "close": {
      "buttonLabel": "تذكير بالإغلاق"
    }
  },
  "el-GR": {
    "close": {
      "buttonLabel": "Κλείσιμο υπενθύμισης"
    }
  },
  "el-CY": {
    "close": {
      "buttonLabel": "Κλείσιμο υπενθύμισης"
    }
  },
  "de-DE": {
    "close": {
      "buttonLabel": "Erinnerung schließen"
    }
  },
  "de-AT": {
    "close": {
      "buttonLabel": "Erinnerung schließen"
    }
  },
  "de-CH": {
    "close": {
      "buttonLabel": "Erinnerung schließen"
    }
  },
  "en-US": {
    "close": {
      "buttonLabel": "Close reminder"
    }
  },
  "en-GB": {
    "close": {
      "buttonLabel": "Close reminder"
    }
  },
  "it-IT": {
    "close": {
      "buttonLabel": "Promemoria di chiusura"
    }
  },
  "fr-FR": {
    "close": {
      "buttonLabel": "Fermer le rappel"
    }
  },
  "es-ES": {
    "close": {
      "buttonLabel": "Cerrar recordatorio"
    }
  },
  "pl-PL": {
    "close": {
      "buttonLabel": "Zamknij przypomnienie"
    }
  },
  "nl-NL": {
    "close": {
      "buttonLabel": "Herinnering sluiten"
    }
  },
  "bg-BG": {
    "close": {
      "buttonLabel": "Затваряне на напомняне"
    }
  },
  "bs-BA": {
    "close": {
      "buttonLabel": "Zatvori podsjetnik"
    }
  },
  "da-DK": {
    "close": {
      "buttonLabel": "Luk påmindelse"
    }
  },
  "et-EE": {
    "close": {
      "buttonLabel": "Sule meeldetuletus"
    }
  },
  "fi-FI": {
    "close": {
      "buttonLabel": "Sulje muistutus"
    }
  },
  "he-IL": {
    "close": {
      "buttonLabel": "תזכורת קרובה"
    }
  },
  "ga-IE": {
    "close": {
      "buttonLabel": "Dún meabhrúchán"
    }
  },
  "ja-JP": {
    "close": {
      "buttonLabel": "リマインダーを閉じる"
    }
  },
  "hr-HR": {
    "close": {
      "buttonLabel": "Zatvori podsjetnik"
    }
  },
  "lv-LV": {
    "close": {
      "buttonLabel": "Aizvērt atgādinājumu"
    }
  },
  "lt-LT": {
    "close": {
      "buttonLabel": "Uždaryti priminimą"
    }
  },
  "pt-PT": {
    "close": {
      "buttonLabel": "Fechar lembrete"
    }
  },
  "ro-RO": {
    "close": {
      "buttonLabel": "Închideți mementoul"
    }
  },
  "ru-RU": {
    "close": {
      "buttonLabel": "Закрыть напоминание"
    }
  },
  "sv-SE": {
    "close": {
      "buttonLabel": "Stäng påminnelse"
    }
  },
  "sk-SK": {
    "close": {
      "buttonLabel": "Zavrieť pripomenutie"
    }
  },
  "cs-CZ": {
    "close": {
      "buttonLabel": "Zavřít připomenutí"
    }
  },
  "hu-HU": {
    "close": {
      "buttonLabel": "Emlékeztető bezárása"
    }
  },
  "fr-BE": {
    "close": {
      "buttonLabel": "Fermer le rappel"
    }
  },
  "fr-LU": {
    "close": {
      "buttonLabel": "Fermer le rappel"
    }
  },
  "mt-MT": {
    "close": {
      "buttonLabel": "Agħlaq it-tfakkira"
    }
  },
  "sl-SI": {
    "close": {
      "buttonLabel": "Zapri opomnik"
    }
  },
  "tr-TR": {
    "close": {
      "buttonLabel": "Hatırlatıcıyı kapat"
    }
  },
  "tr-CY": {
    "close": {
      "buttonLabel": "Hatırlatıcıyı kapat"
    }
  },
  "uk-UA": {
    "close": {
      "buttonLabel": "Закрити нагадування"
    }
  },
  "zh-CN": {
    "close": {
      "buttonLabel": "关闭提醒"
    }
  }
}
</i18n>
